/**
 * Reset Password Dialog
 *
 * Allows admins to set a new password for any user or patient
 * without needing to know their current password.
 */

import { useState } from 'react'
import { router } from '@inertiajs/react'
import { EyeIcon, EyeOffIcon, KeyRoundIcon, RefreshCwIcon } from 'lucide-react'
import { toast } from 'sonner'
import authService from '@/lib/auth'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface ResetPasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  targetName: string
  resetUrl: string
  onSuccess?: () => void
}

const CHARSET = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%'

function generatePassword(length = 16): string {
  let password = ''
  for (let i = 0; i < length; i++) {
    password += CHARSET[Math.floor(Math.random() * CHARSET.length)]
  }
  return password
}

export function ResetPasswordDialog({
  open,
  onOpenChange,
  targetName,
  resetUrl,
  onSuccess,
}: ResetPasswordDialogProps) {
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const handleGenerate = () => {
    const newPassword = generatePassword()
    setPassword(newPassword)
    setPasswordConfirmation(newPassword)
    setShowPassword(true)
    setErrors([])
  }

  const handleClose = () => {
    setPassword('')
    setPasswordConfirmation('')
    setShowPassword(false)
    setErrors([])
    onOpenChange(false)
  }

  const handleSubmit = async () => {
    if (!password) {
      setErrors(['Password is required'])
      return
    }
    if (password !== passwordConfirmation) {
      setErrors(["Password confirmation doesn't match password"])
      return
    }
    if (password.length < 8) {
      setErrors(['Password must be at least 8 characters'])
      return
    }

    setIsSubmitting(true)
    setErrors([])

    try {
      const response = await fetch(resetUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          ...authService.authHeaders(),
        },
        body: JSON.stringify({
          password,
          password_confirmation: passwordConfirmation,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(data.message || 'Password reset successfully')
        handleClose()
        onSuccess?.()
        router.reload({ only: [] })
      } else {
        setErrors(data.errors || ['Failed to reset password'])
      }
    } catch {
      setErrors(['An unexpected error occurred. Please try again.'])
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="bg-primary/10 mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
            <KeyRoundIcon className="text-primary size-6" />
          </div>
          <DialogTitle className="text-center">Reset Password</DialogTitle>
          <DialogDescription className="text-center">
            Set a new password for <span className="font-medium text-foreground">{targetName}</span>.
            They will need to use this password to log in.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="new-password">New Password</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGenerate}
                className="h-7 gap-1.5 text-xs"
              >
                <RefreshCwIcon className="size-3" />
                Generate
              </Button>
            </div>
            <div className="relative">
              <Input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <Input
              id="confirm-password"
              type={showPassword ? 'text' : 'password'}
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              placeholder="Confirm new password"
            />
          </div>

          {errors.length > 0 && (
            <div className="rounded-md bg-destructive/10 p-3">
              {errors.map((error, i) => (
                <p key={i} className="text-sm text-destructive">
                  {error}
                </p>
              ))}
            </div>
          )}

          {showPassword && password && (
            <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
              <p className="text-xs text-amber-800 dark:text-amber-300 font-medium">
                Make sure to copy this password before closing — it won't be shown again.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!password || !passwordConfirmation || isSubmitting}
          >
            {isSubmitting ? 'Resetting...' : 'Reset Password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
